import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import DetailPageLayout from '@/components/layout/DetailPageLayout';
import Breadcrumb from '@/components/common/Breadcrumb';
import CommentSection from '@/components/feature/CommentSection';
import PortfolioPreviewModal from '@/components/common/PortfolioPreviewModal';
import ContactModal from '../modules/ContactModal';
import { useAuth } from '@/hooks/useAuth';
import ConfirmModal from '@/components/common/ConfirmModal';
import { fetchGetPublicProviderDetail, fetchViewProviderContact } from '@/service/api/provider';
import { useBusinessCategoryStore } from '@/stores/businessCategoryStore';
import { useGeoStore } from '@/stores/geoStore';
import { useAssetRenewStore } from '@/stores';
import { formatCurrencyPrice } from '@/utils/currency';

const serviceModeMap: Record<string, string> = {
  FULL_TIME: '全职',
  PART_TIME: '兼职',
  PROJECT: '项目合作',
};

export default function TalentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { isLoggedIn, user, rawUser, hasAuthorizedContactView, isEnterprise } = useAuth();
  const [talent, setTalent] = useState<Api.Provider.ProviderPublicProfileResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 联系方式状态
  const [contactInfo, setContactInfo] = useState<Api.Provider.ProviderContactResponse | null>(null);
  const [loadingContact, setLoadingContact] = useState(false);
  const [contactError, setContactError] = useState<string | null>(null);

  // 代表作品预览状态
  const [previewWork, setPreviewWork] = useState<Api.Provider.ProviderPortfolioItemResponse | null>(null);

  // 联系方式弹窗状态
  const [showContactModal, setShowContactModal] = useState(false);
  const [showNoPermissionModal, setShowNoPermissionModal] = useState(false);

  const { categories, fetchCategories, getCategoryNameByCode } = useBusinessCategoryStore();
  const { provinces, fetchProvinces, getCityNameById } = useGeoStore();

  const categoriesFetchedRef = useRef(false);
  const provincesFetchedRef = useRef(false);
  const fetchedIdRef = useRef<string | null>(null);

  // 认证状态判断：必须为企业账号且认证通过
  const certStatus = (rawUser?.certification_status || user?.verificationStatus || '').toUpperCase();
  const isEnterpriseCertified =
    isLoggedIn &&
    isEnterprise &&
    (certStatus === 'APPROVED' || certStatus === 'PASSED' || certStatus === 'SUCCESS' || user?.verified);

  // 个人账户与自身账户判断（个人账户及本人主页不展示联系与合作按钮）
  const isPersonalAccount = isLoggedIn && !isEnterprise;
  const isSelf = Boolean(
    isLoggedIn &&
    user &&
    ((talent as any)?.account_id ? user.id === (talent as any).account_id : false)
  );
  const shouldHideActions = isPersonalAccount || isSelf;

  useEffect(() => {
    if (!categoriesFetchedRef.current && categories.length === 0) {
      categoriesFetchedRef.current = true;
      fetchCategories().catch((err) => {
        console.error('获取业务类目失败:', err);
        categoriesFetchedRef.current = false;
      });
    }
    if (!provincesFetchedRef.current && provinces.length === 0) {
      provincesFetchedRef.current = true;
      fetchProvinces().catch((err) => {
        console.error('获取省市数据失败:', err);
        provincesFetchedRef.current = false;
      });
    }
  }, [categories.length, fetchCategories, provinces.length, fetchProvinces]);

  useEffect(() => {
    if (!id) return;
    if (fetchedIdRef.current === id) return;
    fetchedIdRef.current = id;

    setLoading(true);
    setError(null);
    fetchGetPublicProviderDetail(id)
      .then((data) => {
        setTalent(data);
      })
      .catch((err) => {
        console.error('获取人才详情失败:', err);
        setError('获取人才详情失败');
        fetchedIdRef.current = null;
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  const assetCache = useAssetRenewStore((state) => state.assetCache);

  // 离场/路由切换时自动清空临时续期缓存
  useEffect(() => {
    return () => {
      useAssetRenewStore.getState().clearCache();
    };
  }, [id]);

  // 监听 talent 数据，自动收集人才头像及代表作品集的资产 ID 并发起续发
  useEffect(() => {
    if (!talent || !id) return;

    // 1. 优先集中将原接口返回的人才头像与作品集附件初始 access_url 注册至 Store 缓存
    const avatarAssetId = talent.avatar?.id || (talent as any).avatar_asset_id;
    const avatarUrl = talent.avatar?.access_url || (talent as any).avatar_url;
    const avatarExpires = talent.avatar?.access_expires;
    if (avatarAssetId && avatarUrl) {
      useAssetRenewStore.getState().setAssetCacheItem(String(avatarAssetId), avatarUrl, avatarExpires);
    }

    const works = talent.portfolio || [];
    works.forEach((work) => {
      const attAssetId = work.attachments?.id || (work.attachments as any)?.asset?.id;
      const attUrl = work.attachments?.access_url || (work.attachments as any)?.url || (work.attachments as any)?.asset?.access_url;
      const attExpires = work.attachments?.access_expires || (work.attachments as any)?.asset?.access_expires;
      if (attAssetId && attUrl) {
        useAssetRenewStore.getState().setAssetCacheItem(String(attAssetId), attUrl, attExpires);
      }
    });

    const renewRequests: Api.Upload.RenewResourceAccessURLsItemRequest[] = [];
    const singleAssetIds: string[] = [];

    if (avatarAssetId) {
      renewRequests.push({
        resource_type: 'PROFILE',
        resource_id: id,
        asset_ids: [String(avatarAssetId)],
      });
    }

    works.forEach((work) => {
      const workId = String(work.id || '');
      const attAssetId = work.attachments?.id || (work.attachments as any)?.asset?.id;
      if (workId && attAssetId) {
        renewRequests.push({
          resource_type: 'PROFILE',
          resource_id: id,
          asset_ids: [String(attAssetId)],
        });
      } else if (attAssetId) {
        singleAssetIds.push(String(attAssetId));
      }
    });

    if (renewRequests.length) {
      useAssetRenewStore.getState().renewBatchResources(renewRequests).catch(() => { });
    }

    singleAssetIds.forEach((assetId) => {
      useAssetRenewStore.getState().renewSingleAsset(assetId).catch(() => { });
    });
  }, [talent, id]);

  const handleFetchContact = async () => {
    if (!id || loadingContact) return;
    setLoadingContact(true);
    setContactError(null);
    try {
      const res = await fetchViewProviderContact(id);
      setContactInfo(res);
      setShowContactModal(true);
    } catch (err: any) {
      console.error('获取联系方式失败:', err);
      setContactError(err instanceof Error ? err.message : '获取联系方式失败');
    } finally {
      setLoadingContact(false);
    }
  };

  const handleContactClick = () => {
    if (!hasAuthorizedContactView) {
      setShowNoPermissionModal(true);
      return;
    }
    if (contactInfo) {
      setShowContactModal(true);
    } else {
      handleFetchContact();
    }
  };

  const handleOpenPreview = (work: Api.Provider.ProviderPortfolioItemResponse) => {
    setPreviewWork(work);
  };

  const handleClosePreview = () => {
    setPreviewWork(null);
  };

  if (loading) {
    return (
      <DetailPageLayout>
        <div className="pt-20 sm:pt-24 flex flex-col items-center justify-center min-h-[60vh]">
          <i className="ri-loader-4-line text-3xl text-primary-500 animate-spin mb-3"></i>
          <p className="text-foreground-500 text-sm">正在加载人才详情...</p>
        </div>
      </DetailPageLayout>
    );
  }

  if (error || !talent) {
    return (
      <DetailPageLayout>
        <div className="pt-20 sm:pt-24 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
              <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
            </div>
            <h2 className="text-lg font-semibold text-foreground-950 mb-2">人才未找到</h2>
            <p className="text-sm text-foreground-500 mb-4">该人才信息可能已下架或不存在</p>
            <Link to="/talents" className="text-sm text-primary-600 hover:text-primary-500 cursor-pointer font-medium">
              返回人才大厅
            </Link>
          </div>
        </div>
      </DetailPageLayout>
    );
  }

  const displayName = talent.display_name || talent.enterprise_name_masked || '创作者';
  const avatarAssetId = talent.avatar?.id || (talent as any).avatar_asset_id;
  const avatarUrl =
    (avatarAssetId && assetCache[avatarAssetId]?.url) ||
    talent.avatar?.access_url ||
    'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&auto=format&fit=crop&q=80';
  const isEnterpriseTalent = talent.provider_type === 'ENTERPRISE';
  const locationName = talent.city_name || getCityNameById(talent.city_code) || '全国/远程';
  const works = talent.portfolio || [];
  const similarTalents = talent.similar_providers || [];

  return (
    <DetailPageLayout
      sidebarWidth="lg:w-[360px] xl:w-[380px]"
      header={
        <>
          {/* Breadcrumb */}
          <div className="pt-16 sm:pt-20">
            <div className="detail-shell">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '人才大厅', href: '/talents' },
                  { label: displayName },
                ]}
              />
            </div>
          </div>

          {/* Talent Header */}
          <section className="bg-white border-b border-gray-200/70 shadow-2xs">
            <div className="detail-shell py-6 md:py-8">
              <div className="flex flex-col lg:flex-row items-center justify-between gap-6 lg:gap-8">
                {/* Avatar & Info */}
                <div className="flex flex-col sm:flex-row items-start gap-5 sm:gap-6 flex-1 min-w-0 w-full">
                  {/* Avatar */}
                  <div className="flex-shrink-0">
                    <div className="relative w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32">
                      <img
                        src={avatarUrl}
                        alt={displayName}
                        className="w-full h-full rounded-3xl object-cover object-top border border-gray-100 shadow-sm"
                      />
                      <span
                        className={`absolute -bottom-1 -right-1 w-6 h-6 sm:w-7 sm:h-7 rounded-full border-[3px] border-white flex items-center justify-center text-xs text-white ${isEnterpriseTalent ? 'bg-indigo-500' : 'bg-primary-500'}`}
                      >
                        <i className={isEnterpriseTalent ? 'ri-building-line' : 'ri-user-3-line'}></i>
                      </span>
                    </div>
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0 pt-1">
                    <div className="flex flex-wrap items-center gap-2.5 mb-2">
                      <h1 className="font-heading font-bold text-xl md:text-2xl lg:text-3xl text-foreground-950 tracking-tight">
                        {displayName}
                      </h1>
                      {isEnterpriseTalent && (
                        <span className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full bg-amber-400/15 text-amber-600 font-medium border border-amber-200/50">
                          <i className="ri-vip-crown-fill text-xs"></i>
                          企业团队
                        </span>
                      )}
                      <span
                        className={`text-xs px-3 py-1 rounded-full font-medium ${talent.seeking_work !== false
                          ? 'bg-primary-500/15 text-primary-600 border border-primary-200/50'
                          : 'bg-foreground-400/15 text-foreground-500 border border-gray-200'
                          }`}
                      >
                        {talent.seeking_work !== false ? '可接单' : '暂满'}
                      </span>
                    </div>

                    <p className="text-sm text-foreground-500 mb-3.5">
                      {isEnterpriseTalent ? '专业企业团队' : '个人创作者'}
                    </p>

                    {/* Specialty Badges */}
                    {talent.category_codes && talent.category_codes.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {talent.category_codes.map((code) => (
                          <span key={code} className="text-xs px-3 py-1 rounded-full font-medium bg-primary-50 text-primary-600 border border-primary-100">
                            {getCategoryNameByCode(code)}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Meta Row */}
                    <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-xs text-foreground-500 pt-1">
                      <span className="flex items-center gap-1.5 bg-gray-50 px-2.5 py-1 rounded-md">
                        <i className="ri-map-pin-line text-primary-500 text-sm"></i>
                        {locationName}
                      </span>
                      {talent.service_mode && (
                        <span className="flex items-center gap-1.5 bg-gray-50 px-2.5 py-1 rounded-md">
                          <i className="ri-briefcase-line text-foreground-400 text-sm"></i>
                          <span className="text-foreground-700 font-medium">
                            {serviceModeMap[talent.service_mode] || talent.service_mode}
                          </span>
                        </span>
                      )}
                      {talent.remote !== undefined && (
                        <span className="flex items-center gap-1.5 bg-gray-50 px-2.5 py-1 rounded-md">
                          <i className="ri-global-line text-primary-500 text-sm"></i>
                          <span className="text-foreground-700 font-medium">{talent.remote ? '支持远程' : '现场履约'}</span>
                        </span>
                      )}
                      {talent.daily_rate !== undefined && (
                        <span className="flex items-center gap-1.5 bg-amber-50/60 px-2.5 py-1 rounded-md border border-amber-100">
                          <i className="ri-money-cny-circle-line text-amber-500 text-sm"></i>
                          <span className="text-amber-700 font-semibold inline-flex items-center">
                            {formatCurrencyPrice(talent.daily_rate, talent.currency)}/天
                          </span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Top Right Actions */}
                {!shouldHideActions && (
                  <div className="flex flex-wrap sm:flex-nowrap items-center justify-center gap-3 shrink-0 w-full sm:w-auto self-center">
                    {/* 查看联系方式 */}
                    {isLoggedIn ? (
                      <button
                        onClick={handleContactClick}
                        disabled={loadingContact}
                        className="px-5 py-2.5 rounded-xl border border-primary-200 text-primary-600 bg-primary-50/50 hover:bg-primary-100/60 text-sm font-semibold transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 justify-center shadow-2xs disabled:opacity-50"
                      >
                        {loadingContact ? (
                          <>
                            <i className="ri-loader-4-line animate-spin"></i>
                            获取中...
                          </>
                        ) : (
                          <>
                            <i className="ri-eye-line"></i>
                            查看联系方式
                          </>
                        )}
                      </button>
                    ) : (
                      <Link
                        to="/auth"
                        className="px-5 py-2.5 rounded-xl border border-primary-200 text-primary-600 bg-primary-50/50 hover:bg-primary-100/60 text-sm font-semibold transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 justify-center shadow-2xs"
                      >
                        <i className="ri-eye-line"></i>
                        查看联系方式
                      </Link>
                    )}

                    {/* 发起合作意向 */}
                    {isEnterpriseCertified ? (
                      <Link
                        to={'/leads/submit/t' + talent.id}
                        className="px-5 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 justify-center shadow-xs hover:shadow-sm"
                      >
                        <i className="ri-chat-smile-2-line"></i>
                        发起合作意向
                      </Link>
                    ) : isLoggedIn ? (
                      <Link
                        to="/certification"
                        className="px-5 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 justify-center shadow-xs hover:shadow-sm"
                      >
                        <i className="ri-chat-smile-2-line"></i>
                        发起合作意向
                      </Link>
                    ) : (
                      <Link
                        to="/auth"
                        className="px-5 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap flex items-center gap-2 justify-center shadow-xs hover:shadow-sm"
                      >
                        <i className="ri-chat-smile-2-line"></i>
                        发起合作意向
                      </Link>
                    )}
                  </div>
                )}
              </div>
            </div>
          </section>
        </>
      }
      sidebar={
        <div className="rounded-2xl p-6 border border-gray-200/60 bg-white shadow-2xs">
          <h3 className="font-semibold text-foreground-950 text-sm mb-4 flex items-center gap-2">
            <i className="ri-dashboard-3-line text-primary-500"></i>
            概览信息
          </h3>
          <div className="grid grid-cols-2 gap-3.5">
            <div className="text-center p-3.5 rounded-xl bg-gray-50/80 border border-gray-100">
              <div className="font-heading font-bold text-base md:text-lg text-primary-600 truncate">
                {formatCurrencyPrice(talent.daily_rate, talent.currency)}
              </div>
              <div className="text-[11px] text-foreground-400 mt-1">日薪单价</div>
            </div>
            <div className="text-center p-3.5 rounded-xl bg-gray-50/80 border border-gray-100">
              <div className="font-heading font-bold text-base md:text-lg text-foreground-950">
                {talent.remote !== false ? '远程' : '现场'}
              </div>
              <div className="text-[11px] text-foreground-400 mt-1">服务方式</div>
            </div>
            <div className="text-center p-3.5 rounded-xl bg-gray-50/80 border border-gray-100">
              <div className="font-heading font-bold text-base md:text-lg text-foreground-950">
                {talent.seeking_work !== false ? '可接单' : '暂满'}
              </div>
              <div className="text-[11px] text-foreground-400 mt-1">接单状态</div>
            </div>
            <div className="text-center p-3.5 rounded-xl bg-gray-50/80 border border-gray-100">
              <div className="font-heading font-bold text-base md:text-lg text-foreground-950">
                {works.length}
              </div>
              <div className="text-[11px] text-foreground-400 mt-1">代表作品</div>
            </div>
          </div>
        </div>
      }
      extra={
        similarTalents.length > 0 ? (
          <section className="pt-2 pb-4">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-primary-50 text-primary-600 flex items-center justify-center font-bold">
                  <i className="ri-team-line text-lg"></i>
                </div>
                <div>
                  <h2 className="font-heading font-bold text-base text-foreground-950">相似人才推荐</h2>
                  <p className="text-xs text-foreground-400">同领域的优秀创作者与服务商团队</p>
                </div>
              </div>
              <span className="text-xs text-primary-600 bg-primary-50 px-2.5 py-1 rounded-full font-medium">
                {similarTalents.length} 位推荐
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {similarTalents.map((st) => {
                const stName = st.display_name || st.enterprise_name_masked || '创作者';
                const stAvatar = st.avatar?.access_url || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&auto=format&fit=crop&q=80';
                const isEnterprise = st.provider_type === 'ENTERPRISE';

                return (
                  <Link
                    key={st.id}
                    to={`/talents/${st.id}`}
                    className="rounded-xl p-4 sm:p-5 border border-gray-200/70 hover:border-primary-300 transition-all duration-200 cursor-pointer group bg-white hover:shadow-md flex flex-col justify-between"
                  >
                    <div>
                      <div className="flex items-center gap-3.5 mb-3">
                        <div className="relative shrink-0">
                          <img
                            src={stAvatar}
                            alt={stName}
                            className="w-12 h-12 rounded-xl object-cover object-top border border-gray-100 shadow-2xs"
                            loading="lazy"
                          />
                          <span className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white flex items-center justify-center text-[9px] text-white ${isEnterprise ? 'bg-indigo-500' : 'bg-primary-500'}`}>
                            <i className={isEnterprise ? 'ri-building-line' : 'ri-user-3-line'}></i>
                          </span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="font-semibold text-foreground-950 text-sm group-hover:text-primary-600 transition-colors truncate flex items-center gap-1">
                            <span>{stName}</span>
                          </h3>
                          <p className="text-xs text-foreground-400 truncate mt-0.5">
                            {isEnterprise ? '企业团队' : '个人创作者'}
                          </p>
                        </div>
                      </div>

                      {st.category_codes && st.category_codes.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-3">
                          {st.category_codes.slice(0, 2).map((code) => (
                            <span key={code} className="text-[11px] px-2 py-0.5 rounded-md bg-primary-50 text-primary-600 font-medium">
                              {getCategoryNameByCode(code)}
                            </span>
                          ))}
                        </div>
                      )}

                      <p className="text-xs text-foreground-500 line-clamp-2 leading-relaxed mb-3">
                        {st.intro_summary || '暂无个人简介...'}
                      </p>
                    </div>

                    <div className="pt-2 border-t border-gray-50 flex items-center justify-between text-xs text-foreground-400 group-hover:text-primary-600 transition-colors">
                      <span className="font-medium">查看主页</span>
                      <i className="ri-arrow-right-line transform group-hover:translate-x-1 transition-transform"></i>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        ) : null
      }
      comments={
        talent.id ? (
          <CommentSection
            targetType="PROVIDER_PROFILE"
            targetId={talent.id}
            title="人才评价与留言"
            isTargetAuthor={isSelf}
          />
        ) : null
      }
      modals={
        <>
          {/* Representative Work Preview Modal */}
          <PortfolioPreviewModal
            open={Boolean(previewWork)}
            title={previewWork?.name}
            intro={previewWork?.intro}
            isVideo={
              previewWork?.attachments?.category === 'PORTFOLIO_VIDEO' ||
              (previewWork?.attachments?.category || '').toUpperCase().includes('VIDEO')
            }
            mediaUrl={previewWork?.attachments?.access_url}
            onClose={handleClosePreview}
          />

          {/* 联系方式弹窗 */}
          <ContactModal
            open={showContactModal}
            contactInfo={contactInfo}
            onClose={() => setShowContactModal(false)}
          />

          {/* 暂无权限提示弹窗 */}
          <ConfirmModal
            open={showNoPermissionModal}
            type="warning"
            title="暂无查看权限"
            content="您暂无权限查看该人才的联系方式，请联系管理员开通权限。"
            confirmText="我知道了"
            cancelText=""
            onConfirm={() => setShowNoPermissionModal(false)}
            onCancel={() => setShowNoPermissionModal(false)}
          />
        </>
      }
    >
      {/* About */}
      <section className="rounded-2xl p-6 md:p-8 border border-gray-200/60 bg-white shadow-2xs">
        <h2 className="font-heading font-bold text-lg text-foreground-950 mb-4 flex items-center gap-2">
          <i className="ri-user-3-line text-primary-500"></i>
          个人简介
        </h2>
        <p className="text-sm text-foreground-600 leading-relaxed whitespace-pre-line">
          {talent.intro || '暂无详细个人简介说明'}
        </p>
      </section>

      {/* Service Scope */}
      {talent.service_scope && (
        <section className="rounded-2xl p-6 md:p-8 border border-gray-200/60 bg-white shadow-2xs">
          <h2 className="font-heading font-bold text-lg text-foreground-950 mb-4 flex items-center gap-2">
            <i className="ri-file-text-line text-primary-500"></i>
            服务范围
          </h2>
          <p className="text-sm text-foreground-600 leading-relaxed whitespace-pre-line">
            {talent.service_scope}
          </p>
        </section>
      )}

      {/* Tags */}
      {talent.skill_tags && talent.skill_tags.length > 0 && (
        <section className="rounded-2xl p-6 md:p-8 border border-gray-200/60 bg-white shadow-2xs">
          <h2 className="font-heading font-bold text-lg text-foreground-950 mb-4 flex items-center gap-2">
            <i className="ri-hashtag text-primary-500"></i>
            技能标签
          </h2>
          <div className="flex flex-wrap gap-2.5">
            {talent.skill_tags.map((tag) => (
              <span
                key={tag}
                className="text-sm px-4 py-2 rounded-xl bg-gray-50 text-foreground-700 border border-gray-200/70 cursor-default font-medium"
              >
                {tag}
              </span>
            ))}
          </div>
        </section>
      )}

      {/* Works / Portfolio */}
      {works.length > 0 && (
        <section className="rounded-2xl p-6 md:p-8 border border-gray-200/60 bg-white shadow-2xs">
          <h2 className="font-heading font-bold text-lg text-foreground-950 mb-6 flex items-center gap-2">
            <i className="ri-play-circle-line text-primary-500"></i>
            代表作品
            <span className="text-xs font-normal text-foreground-400">({works.length} 部)</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
            {works.map((work) => {
              const workAssetId = work.attachments?.id || (work.attachments as any)?.asset?.id;
              const coverUrl = (workAssetId && assetCache[workAssetId]?.url) || work.attachments?.access_url;
              const isVideo =
                work.attachments?.category === 'PORTFOLIO_VIDEO' ||
                (work.attachments?.category || '').toUpperCase().includes('VIDEO');
              return (
                <div
                  key={work.id || work.name}
                  onClick={() => handleOpenPreview(work)}
                  className="rounded-2xl overflow-hidden group cursor-pointer border border-gray-200/60 hover:border-primary-300 hover:shadow-md transition-all bg-white flex flex-col"
                >
                  {/* Cover Image / Video */}
                  <div className="relative aspect-video overflow-hidden bg-gray-100 flex items-center justify-center">
                    {coverUrl ? (
                      isVideo ? (
                        <video
                          src={coverUrl}
                          muted
                          playsInline
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 pointer-events-none"
                        />
                      ) : (
                        <img
                          src={coverUrl}
                          alt={work.name}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      )
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center bg-gray-50 text-gray-400 group-hover:bg-primary-50/50 transition-colors">
                        <i className={`${isVideo ? 'ri-vidicon-line' : 'ri-image-line'} text-3xl mb-1 text-gray-300 group-hover:text-primary-400 transition-colors`}></i>
                        <span className="text-[11px] font-medium">{isVideo ? '点击播放视频' : '点击查看作品'}</span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/60 to-transparent"></div>
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="w-12 h-12 rounded-full bg-white/90 shadow-lg flex items-center justify-center transform group-hover:scale-110 transition-transform">
                        <i className={`${isVideo ? 'ri-play-fill text-2xl text-primary-600 ml-0.5' : 'ri-zoom-in-line text-xl text-primary-600'}`}></i>
                      </div>
                    </div>
                    {isVideo && (
                      <div className="absolute top-3 right-3 px-2 py-1 rounded-md bg-black/60 backdrop-blur-sm text-[10px] text-white font-medium flex items-center gap-1">
                        <i className="ri-vidicon-line"></i>
                        视频
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-4 flex-1 flex flex-col justify-between">
                    <h3 className="font-semibold text-foreground-950 text-sm leading-snug mb-1 line-clamp-1 group-hover:text-primary-600 transition-colors">
                      {work.name}
                    </h3>
                    {work.intro && (
                      <p className="text-xs text-foreground-500 line-clamp-2">{work.intro}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </DetailPageLayout>
  );
}